import React from 'react';
import { useAppTheme } from '../../context/ThemeContext';

interface Option {
  value: string;
  label: string;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  helperText?: string;
  label?: string;
  placeholder?: string;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  error,
  helperText,
  label,
  placeholder,
  className = '',
  ...props
}) => {
  const { themeConfig } = useAppTheme();

  const getSelectStyles = () => {
    const baseStyles = 'block w-full rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
    const errorStyles = error
      ? `border-[${themeConfig.error}] focus:ring-[${themeConfig.error}]`
      : `border-[${themeConfig.accent}] focus:ring-[${themeConfig.primary}]`;
    
    return `${baseStyles} ${errorStyles} pl-3 pr-10 py-2 bg-[${themeConfig.surface}] text-[${themeConfig.text}] border ${className}`;
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className={`block text-sm font-medium text-[${themeConfig.text}]`}>
          {label}
        </label>
      )}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={getSelectStyles()}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      {error && (
        <p className={`text-sm text-[${themeConfig.error}]`}>
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className={`text-sm text-[${themeConfig.text}] opacity-70`}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default Select; 